import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Pencil, RefreshCw, Check, Send, Clock, XCircle,
  Monitor, Smartphone, Eye, Mail, CalendarClock, AlertTriangle, Users, FileText, ChevronLeft,
} from "lucide-react";

// ---- helpers ----
function fmtPT(iso, withTime = true) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short", day: "numeric", year: "numeric",
      ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}),
    }) + (withTime ? " PT" : "");
  } catch { return iso; }
}

function tzOffsetMin(date, tz) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false, year: "numeric", month: "2-digit",
    day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p = dtf.formatToParts(date).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  const asIfUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return (asIfUTC - date.getTime()) / 60000;
}
// Interpret "YYYY-MM-DD" + "HH:mm" as America/Los_Angeles wall time -> UTC ISO.
function ptWallToUtcIso(dateStr, timeStr) {
  const asUTC = new Date(`${dateStr}T${timeStr}:00Z`);
  const offMin = tzOffsetMin(asUTC, "America/Los_Angeles");
  return new Date(asUTC.getTime() - offMin * 60000).toISOString();
}

const STATUS_META = {
  draft: { label: "Draft", cls: "border-[#B08D57] text-[#8A6A2E] bg-[#F3ECDD]", icon: FileText },
  approved: { label: "Approved", cls: "border-[#2E7D57] text-[#1F6144] bg-[#E4F0E9]", icon: Check },
  scheduled: { label: "Scheduled", cls: "border-[#2D2D2D] text-[#1E1E1E] bg-[#E4E9EC]", icon: CalendarClock },
  sending: { label: "Sending…", cls: "border-[#B08D57] text-[#8A6A2E] bg-[#F3ECDD]", icon: Loader2 },
  sent: { label: "Sent", cls: "border-[#2E7D57] text-[#1F6144] bg-[#E4F0E9]", icon: Send },
  failed: { label: "Failed", cls: "border-[#B4443A] text-[#8F2E26] bg-[#F3E1DE]", icon: XCircle },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  const Icon = m.icon;
  return (
    <span data-testid="newsletter-status-badge" className={`inline-flex items-center gap-1.5 px-2.5 py-1 border-2 font-head uppercase tracking-wide text-xs ${m.cls}`}>
      <Icon size={13} className={status === "sending" ? "animate-spin" : ""} /> {m.label}
    </span>
  );
}

const EDIT_FIELDS = [
  ["subject", "Subject", "input"],
  ["preview_text", "Preview text", "input"],
  ["headline", "Headline", "input"],
  ["note_from_kyto", "A Note From Kyto", "textarea"],
  ["the_read", "The Read", "textarea"],
  ["one_thing", "One Thing to Take Into Practice", "textarea"],
  ["sign_off", "Sign-off", "textarea"],
];

export default function NewsletterAdmin({ api }) {
  const [issues, setIssues] = useState(null);
  const [subCount, setSubCount] = useState(null);
  const [sendEnabled, setSendEnabled] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState("");           // action key while a request runs
  const [generating, setGenerating] = useState(false);

  // preview
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [previewLoading, setPreviewLoading] = useState(false);

  // edit
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editNotice, setEditNotice] = useState("");

  // dialogs
  const [regenOpen, setRegenOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("09:00");
  const [sendOpen, setSendOpen] = useState(false);
  const [sendConfirm, setSendConfirm] = useState("");
  const [sendResult, setSendResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, c, cfg] = await Promise.all([
        api.get("/admin/newsletters"),
        api.get("/admin/newsletters/subscribers/count"),
        api.get("/admin/newsletters/config"),
      ]);
      setIssues(l.data.issues || []);
      setSubCount(c.data.active_subscribers ?? 0);
      setSendEnabled(!!cfg.data.send_enabled);
    } catch { toast.error("Couldn't load newsletter data."); setIssues([]); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const current = issues && (issues.find((i) => i.id === selectedId) || issues[0]) || null;
  const scheduledIssue = issues && issues.find((i) => i.status === "scheduled");

  const openPreview = async (id) => {
    setPreviewLoading(true);
    setPreviewHtml("");
    try {
      const { data } = await api.get(`/admin/newsletters/${id}/preview`, { responseType: "text", transformResponse: (r) => r });
      setPreviewHtml(typeof data === "string" ? data : String(data));
    } catch { toast.error("Preview failed to load."); setPreviewHtml(null); }
    finally { setPreviewLoading(false); }
  };

  const doGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/newsletters/generate");
      if (data.status === "insufficient_content") {
        toast.message("Not enough content yet", { description: data.message });
      } else if (data.issue) {
        toast.success("Draft generated.");
        await load();
        setSelectedId(data.issue.id);
        openPreview(data.issue.id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed. Try again.");
    } finally { setGenerating(false); }
  };

  const startEdit = async () => {
    if (!current) return;
    setBusy("edit-load");
    try {
      const { data } = await api.get(`/admin/newsletters/${current.id}`);
      const c = data.content || {};
      setEditForm({
        subject: c.subject ?? data.subject ?? "",
        preview_text: c.preview_text ?? data.preview_text ?? "",
        headline: c.headline ?? data.headline ?? "",
        note_from_kyto: c.note_from_kyto ?? "",
        the_read: c.the_read ?? "",
        one_thing: c.one_thing ?? "",
        sign_off: c.sign_off ?? "",
        both_sides: {
          coach: c.both_sides?.coach ?? "",
          parent: c.both_sides?.parent ?? "",
          voluntold_take: c.both_sides?.voluntold_take ?? "",
        },
        _refs: { also_this_week: c.also_this_week || [], from_voluntold: c.from_voluntold || {} },
      });
      setEditNotice("");
      setEditing(true);
    } catch { toast.error("Couldn't load the issue for editing (it may predate structured editing — try Regenerate)."); }
    finally { setBusy(""); }
  };

  const saveEdit = async () => {
    setBusy("save");
    try {
      const payload = {
        subject: editForm.subject, preview_text: editForm.preview_text, headline: editForm.headline,
        note_from_kyto: editForm.note_from_kyto, the_read: editForm.the_read,
        one_thing: editForm.one_thing, sign_off: editForm.sign_off, both_sides: editForm.both_sides,
      };
      const wasApprovedOrScheduled = ["approved", "scheduled"].includes(current.status);
      await api.patch(`/admin/newsletters/${current.id}`, payload);
      await load();
      if (wasApprovedOrScheduled) setEditNotice("Changes saved. This issue is back to Draft and requires approval again.");
      else setEditNotice("Changes saved.");
      toast.success("Saved.");
      setEditing(false);
      openPreview(current.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(""); }
  };

  const doRegenerate = async () => {
    setRegenOpen(false);
    setBusy("regen");
    try {
      const { data } = await api.post(`/admin/newsletters/${current.id}/regenerate`);
      toast.success("Regenerated — this is a fresh draft.");
      await load();
      if (data.issue) openPreview(data.issue.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Regenerate failed."); }
    finally { setBusy(""); }
  };

  const doApprove = async () => {
    setBusy("approve");
    try { await api.post(`/admin/newsletters/${current.id}/approve`); toast.success("Approved — ready to send (not sent yet)."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed."); }
    finally { setBusy(""); }
  };

  const doSendTest = async () => {
    const email = testEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid test email."); return; }
    setBusy("test");
    try {
      const { data } = await api.post(`/admin/newsletters/${current.id}/send-test`, { email });
      if (data.status === "simulated") toast.message("Simulated test send", { description: `Sending is OFF — no real email was sent to ${email}.` });
      else toast.success(`Test sent to ${email}.`);
      setTestOpen(false); setTestEmail("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test send failed."); }
    finally { setBusy(""); }
  };

  const doSchedule = async () => {
    if (!schedDate || !schedTime) { toast.error("Pick a date and time."); return; }
    const iso = ptWallToUtcIso(schedDate, schedTime);
    if (new Date(iso).getTime() <= Date.now()) { toast.error("Pick a future time."); return; }
    setBusy("schedule");
    try { await api.post(`/admin/newsletters/${current.id}/schedule`, { scheduled_for: iso }); toast.success("Scheduled."); setScheduleOpen(false); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Schedule failed."); }
    finally { setBusy(""); }
  };

  const doCancelSchedule = async () => {
    setBusy("cancel");
    try { await api.post(`/admin/newsletters/${current.id}/cancel-schedule`); toast.success("Schedule cancelled — back to Approved."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed."); }
    finally { setBusy(""); }
  };

  const doSendNow = async () => {
    if (sendConfirm !== "SEND") return;
    setBusy("send");
    try {
      const { data } = await api.post(`/admin/newsletters/${current.id}/send`);
      setSendResult(data);
      toast.success(data.send_enabled ? "Broadcast complete." : "Simulated broadcast complete.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed."); setSendOpen(false); }
    finally { setBusy(""); setSendConfirm(""); }
  };

  if (issues === null) {
    return <div className="mt-10 text-[#6E6E6E] flex items-center gap-2" data-testid="newsletter-loading"><Loader2 className="animate-spin" size={18} /> Loading newsletter…</div>;
  }

  const st = current?.status;
  const canEdit = current && !["sent", "sending"].includes(st);
  const btn = "inline-flex items-center gap-2 font-head uppercase tracking-wide text-xs px-4 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const btnPrimary = `${btn} bg-[#E8A33D] text-[#1A1A1A] hover:bg-[#D6922C]`;
  const btnDark = `${btn} bg-[#2D2D2D] text-[#F3F1EC] hover:bg-[#242424]`;
  const btnGhost = `${btn} border-2 border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D] hover:text-[#2D2D2D]`;

  return (
    <div className="mt-8 space-y-8" data-testid="newsletter-admin">
      {/* Dashboard */}
      <div>
        <span className="kicker">Newsletter</span>
        <h2 className="font-head font-semibold uppercase text-2xl text-[#1E1E1E]">VoluntoldHQ's Scouting Report</h2>
        {sendEnabled === false && (
          <div className="mt-3 inline-flex items-center gap-2 border-2 border-[#B08D57] bg-[#F3ECDD] px-3 py-1.5 text-xs text-[#8A6A2E] font-head uppercase tracking-wide" data-testid="newsletter-sim-banner">
            <AlertTriangle size={14} /> Simulation mode — sending is OFF, no real emails go out
          </div>
        )}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="border-2 border-[#1E1E1E] bg-[#E7E3D9] p-4" data-testid="newsletter-stat-subscribers">
            <div className="flex items-center gap-2 text-[#6E6E6E]"><Users size={15} /><span className="kicker">Active subscribers</span></div>
            <p className="mt-2 font-head font-semibold text-3xl text-[#1E1E1E]">{subCount ?? "—"}</p>
          </div>
          <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-4" data-testid="newsletter-stat-latest">
            <span className="kicker text-[#6E6E6E]">Latest issue</span>
            <p className="mt-2 font-head text-[#1E1E1E]">{current ? `#${current.issue_number} · ${current.status}` : "None yet"}</p>
          </div>
          <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-4" data-testid="newsletter-stat-scheduled">
            <span className="kicker text-[#6E6E6E]">Next scheduled</span>
            <p className="mt-2 font-head text-[#1E1E1E] text-sm">{scheduledIssue ? `#${scheduledIssue.issue_number} · ${fmtPT(scheduledIssue.scheduled_for)}` : "None"}</p>
          </div>
        </div>
      </div>

      {/* Generate / empty state */}
      {issues.length === 0 ? (
        <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-8 text-center" data-testid="newsletter-empty">
          <p className="font-head uppercase text-[#1E1E1E]">No issues yet</p>
          <p className="text-sm text-[#6E6E6E] mt-1 mb-4">Generate this week's issue from your published Voluntold content.</p>
          <button onClick={doGenerate} disabled={generating} className={btnPrimary} data-testid="newsletter-generate-btn">
            {generating ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate Newsletter</>}
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={doGenerate} disabled={generating} className={btnDark} data-testid="newsletter-generate-btn">
            {generating ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate new issue</>}
          </button>
          {selectedId && issues[0] && selectedId !== issues[0].id && (
            <button onClick={() => setSelectedId(null)} className={btnGhost} data-testid="newsletter-back-latest"><ChevronLeft size={14} /> Back to latest</button>
          )}
        </div>
      )}

      {/* Current issue card */}
      {current && (
        <div className="border-2 border-[#1E1E1E] bg-[#F3F1EC]" data-testid="newsletter-current-card">
          <div className="p-5 border-b-2 border-[#D3CFC0] flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="kicker text-[#E8A33D]">Issue #{current.issue_number}</span>
                <StatusBadge status={current.status} />
                <span className="kicker text-[#6E6E6E]">· {current.source_type}</span>
              </div>
              <h3 className="mt-2 font-head font-semibold text-xl text-[#1E1E1E]" data-testid="newsletter-current-subject">{current.subject || "(no subject)"}</h3>
              <p className="text-sm text-[#6E6E6E] mt-0.5">{current.headline}</p>
            </div>
          </div>

          <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-y-3 gap-x-4 text-xs">
            <Meta label="Generated" value={fmtPT(current.generated_at, false)} />
            <Meta label="Approved" value={current.approved_at ? fmtPT(current.approved_at, false) : "—"} />
            <Meta label="Scheduled" value={current.scheduled_for ? fmtPT(current.scheduled_for) : "—"} />
            <Meta label="Sent" value={current.sent_at ? fmtPT(current.sent_at) : "—"} />
          </div>

          {["sent", "failed"].includes(st) && (
            <div className="px-5 pb-5 grid grid-cols-3 gap-4" data-testid="newsletter-send-stats">
              <Meta label="Recipients" value={current.recipient_count ?? 0} big />
              <Meta label="Delivered" value={current.successful_count ?? 0} big />
              <Meta label="Failed" value={current.failed_count ?? 0} big />
            </div>
          )}

          {editNotice && (
            <div className="mx-5 mb-4 border-2 border-[#B08D57] bg-[#F3ECDD] px-3 py-2 text-xs text-[#8A6A2E]" data-testid="newsletter-edit-notice">{editNotice}</div>
          )}

          {/* actions */}
          <div className="px-5 pb-5 flex flex-wrap gap-2">
            <button onClick={() => openPreview(current.id)} className={btnGhost} data-testid="newsletter-preview-btn"><Eye size={14} /> Preview</button>
            {canEdit && <button onClick={startEdit} disabled={busy === "edit-load"} className={btnGhost} data-testid="newsletter-edit-btn"><Pencil size={14} /> Edit</button>}
            {st === "draft" && <button onClick={() => setRegenOpen(true)} disabled={busy === "regen"} className={btnGhost} data-testid="newsletter-regenerate-btn"><RefreshCw size={14} /> Regenerate</button>}
            {st === "draft" && <button onClick={doApprove} disabled={busy === "approve"} className={btnDark} data-testid="newsletter-approve-btn"><Check size={14} /> Approve</button>}
            {["draft", "approved", "scheduled"].includes(st) && <button onClick={() => { setTestOpen(true); setTestEmail(""); }} className={btnGhost} data-testid="newsletter-sendtest-btn"><Mail size={14} /> Send Test</button>}
            {st === "approved" && <button onClick={() => { setSchedDate(""); setSchedTime("09:00"); setScheduleOpen(true); }} className={btnGhost} data-testid="newsletter-schedule-btn"><CalendarClock size={14} /> Schedule</button>}
            {st === "scheduled" && <button onClick={doCancelSchedule} disabled={busy === "cancel"} className={btnGhost} data-testid="newsletter-cancel-schedule-btn"><XCircle size={14} /> Cancel Schedule</button>}
            {st === "approved" && <button onClick={() => { setSendResult(null); setSendConfirm(""); setSendOpen(true); }} className={btnPrimary} data-testid="newsletter-sendnow-btn"><Send size={14} /> Send Now</button>}
          </div>

          {st === "approved" && <p className="px-5 pb-5 -mt-2 text-xs text-[#6E6E6E]">Approved means ready to send. It has not been sent yet.</p>}
        </div>
      )}

      {/* Edit panel */}
      {editing && editForm && (
        <div className="border-2 border-[#2D2D2D] bg-[#F3F1EC] p-5" data-testid="newsletter-edit-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-semibold uppercase text-lg text-[#1E1E1E]">Edit issue #{current.issue_number}</h3>
            <button onClick={() => setEditing(false)} className="text-[#6E6E6E] hover:text-[#1E1E1E]"><XCircle size={18} /></button>
          </div>
          <div className="space-y-4">
            {EDIT_FIELDS.map(([k, label, type]) => (
              <div key={k}>
                <label className="kicker">{label}</label>
                {type === "input" ? (
                  <input value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} data-testid={`newsletter-edit-${k}`}
                    className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 focus:outline-none focus:border-[#2D2D2D]" />
                ) : (
                  <textarea rows={k === "the_read" ? 6 : 3} value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} data-testid={`newsletter-edit-${k}`}
                    className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 text-sm focus:outline-none focus:border-[#2D2D2D]" />
                )}
              </div>
            ))}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[["coach", "Both Sides · Coach"], ["parent", "Both Sides · Player/Parent"], ["voluntold_take", "Both Sides · Voluntold Take"]].map(([k, label]) => (
                <div key={k}>
                  <label className="kicker">{label}</label>
                  <textarea rows={3} value={editForm.both_sides[k]} onChange={(e) => setEditForm({ ...editForm, both_sides: { ...editForm.both_sides, [k]: e.target.value } })} data-testid={`newsletter-edit-both-${k}`}
                    className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 text-sm focus:outline-none focus:border-[#2D2D2D]" />
                </div>
              ))}
            </div>
            {(editForm._refs.also_this_week?.length > 0 || editForm._refs.from_voluntold?.name) && (
              <p className="text-xs text-[#6E6E6E] border-t border-[#D3CFC0] pt-3">
                "Also This Week" and "From Voluntold" link to verified Voluntold content and are managed via Regenerate.
              </p>
            )}
          </div>
          <div className="mt-5 flex gap-2">
            <button onClick={saveEdit} disabled={busy === "save"} className={btnDark} data-testid="newsletter-edit-save"><Check size={14} /> {busy === "save" ? "Saving…" : "Save changes"}</button>
            <button onClick={() => setEditing(false)} className={btnGhost}>Cancel</button>
          </div>
        </div>
      )}

      {/* Preview */}
      {(previewLoading || previewHtml) && (
        <div className="border-2 border-[#1E1E1E] bg-[#E7E3D9]" data-testid="newsletter-preview-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#D3CFC0]">
            <span className="kicker">Preview</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPreviewDevice("desktop")} data-testid="newsletter-preview-desktop"
                className={`p-2 border-2 ${previewDevice === "desktop" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E]"}`}><Monitor size={15} /></button>
              <button onClick={() => setPreviewDevice("mobile")} data-testid="newsletter-preview-mobile"
                className={`p-2 border-2 ${previewDevice === "mobile" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E]"}`}><Smartphone size={15} /></button>
              <button onClick={() => setPreviewHtml(null)} className="text-[#6E6E6E] hover:text-[#1E1E1E] ml-1"><XCircle size={18} /></button>
            </div>
          </div>
          <div className="p-4 overflow-auto flex justify-center bg-[#DDD8CC]">
            {previewLoading ? (
              <div className="py-16 text-[#6E6E6E] flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Rendering…</div>
            ) : (
              <iframe title="Newsletter preview" data-testid="newsletter-preview-iframe" sandbox="allow-same-origin"
                srcDoc={previewHtml} className="bg-white border border-[#C9C3B6]"
                style={{ width: previewDevice === "desktop" ? 600 : 375, maxWidth: "100%", height: 640 }} />
            )}
          </div>
        </div>
      )}

      {/* Past issues */}
      {issues.length > 0 && (
        <div data-testid="newsletter-past-issues">
          <h3 className="font-head font-semibold uppercase text-lg text-[#1E1E1E] border-b-2 border-[#D3CFC0] pb-2 mb-3">Past issues</h3>
          <div className="overflow-x-auto border-2 border-[#D3CFC0]">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[#E7E3D9] text-[#6E6E6E]">
                <tr className="text-left font-head uppercase tracking-wide text-xs">
                  <th className="px-3 py-2">#</th><th className="px-3 py-2">Subject</th><th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Generated</th><th className="px-3 py-2">Sent</th>
                  <th className="px-3 py-2 text-right">Rcp/OK/Fail</th><th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D3CFC0]">
                {issues.map((i) => (
                  <tr key={i.id} className={`${i.id === current?.id ? "bg-[#F3ECDD]" : "bg-[#F3F1EC]"}`} data-testid={`newsletter-row-${i.issue_number}`}>
                    <td className="px-3 py-2 font-head text-[#1E1E1E]">{i.issue_number}</td>
                    <td className="px-3 py-2 text-[#1E1E1E] max-w-[220px] truncate">{i.subject || "—"}</td>
                    <td className="px-3 py-2"><StatusBadge status={i.status} /></td>
                    <td className="px-3 py-2 text-[#6E6E6E]">{fmtPT(i.generated_at, false)}</td>
                    <td className="px-3 py-2 text-[#6E6E6E]">{i.sent_at ? fmtPT(i.sent_at, false) : "—"}</td>
                    <td className="px-3 py-2 text-right text-[#6E6E6E]">{["sent", "failed"].includes(i.status) ? `${i.recipient_count}/${i.successful_count}/${i.failed_count}` : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => { setSelectedId(i.id); openPreview(i.id); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="text-[#2D2D2D] link-line font-head uppercase text-xs" data-testid={`newsletter-open-${i.issue_number}`}>Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- Dialogs ---- */}
      {regenOpen && (
        <Modal onClose={() => setRegenOpen(false)} testId="newsletter-regen-dialog" title="Regenerate this issue?">
          <p className="text-sm text-[#6E6E6E]">This replaces the current draft copy using the available Voluntold source content. The issue number stays the same.</p>
          <div className="mt-5 flex gap-2 justify-end">
            <button onClick={() => setRegenOpen(false)} className={btnGhost}>Cancel</button>
            <button onClick={doRegenerate} className={btnDark} data-testid="newsletter-regen-confirm"><RefreshCw size={14} /> Regenerate</button>
          </div>
        </Modal>
      )}

      {testOpen && (
        <Modal onClose={() => setTestOpen(false)} testId="newsletter-test-dialog" title="Send a test">
          <p className="text-sm text-[#6E6E6E] mb-3">Sends this issue to one address only. {sendEnabled === false ? "Sending is OFF, so this will be simulated." : "A real test email will be sent."}</p>
          <label className="kicker">Test email</label>
          <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@email.com" data-testid="newsletter-test-email"
            className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 focus:outline-none focus:border-[#2D2D2D]" />
          <div className="mt-5 flex gap-2 justify-end">
            <button onClick={() => setTestOpen(false)} className={btnGhost}>Cancel</button>
            <button onClick={doSendTest} disabled={busy === "test"} className={btnDark} data-testid="newsletter-test-send"><Mail size={14} /> {busy === "test" ? "Sending…" : "Send test"}</button>
          </div>
        </Modal>
      )}

      {scheduleOpen && (
        <Modal onClose={() => setScheduleOpen(false)} testId="newsletter-schedule-dialog" title="Schedule this issue">
          <p className="text-sm text-[#6E6E6E] mb-3">Times are America/Los_Angeles. Scheduling stores the intent — it will not send in this phase.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="kicker">Date</label>
              <input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="newsletter-schedule-date"
                className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 focus:outline-none focus:border-[#2D2D2D]" />
            </div>
            <div className="flex-1">
              <label className="kicker">Time (PT)</label>
              <input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} data-testid="newsletter-schedule-time"
                className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 focus:outline-none focus:border-[#2D2D2D]" />
            </div>
          </div>
          <div className="mt-5 flex gap-2 justify-end">
            <button onClick={() => setScheduleOpen(false)} className={btnGhost}>Cancel</button>
            <button onClick={doSchedule} disabled={busy === "schedule"} className={btnDark} data-testid="newsletter-schedule-confirm"><CalendarClock size={14} /> {busy === "schedule" ? "Scheduling…" : "Schedule"}</button>
          </div>
        </Modal>
      )}

      {sendOpen && (
        <Modal onClose={() => { if (busy !== "send") setSendOpen(false); }} testId="newsletter-send-dialog"
          title={sendEnabled ? "Send this newsletter now" : "Send Now (Simulation Mode)"}>
          {sendResult ? (
            <div data-testid="newsletter-send-result">
              <div className="flex items-center gap-2 text-[#1F6144] font-head uppercase tracking-wide text-sm mb-3"><Check size={16} /> {sendResult.send_enabled ? "Broadcast complete" : "Simulated — no real emails sent"}</div>
              <div className="grid grid-cols-3 gap-3">
                <Meta label="Recipients" value={sendResult.recipient_count} big />
                <Meta label="Delivered" value={sendResult.successful_count} big />
                <Meta label="Failed" value={sendResult.failed_count} big />
              </div>
              <div className="mt-5 flex justify-end"><button onClick={() => setSendOpen(false)} className={btnDark}>Done</button></div>
            </div>
          ) : (
            <>
              {sendEnabled === false ? (
                <div className="border-2 border-[#B08D57] bg-[#F3ECDD] px-3 py-2 text-xs text-[#8A6A2E] mb-3 flex items-center gap-2" data-testid="newsletter-send-sim-note">
                  <AlertTriangle size={14} /> SIMULATION MODE — no real emails will be sent.
                </div>
              ) : (
                <div className="border-2 border-[#B4443A] bg-[#F3E1DE] px-3 py-2 text-xs text-[#8F2E26] mb-3 flex items-center gap-2" data-testid="newsletter-send-real-note">
                  <AlertTriangle size={14} /> LIVE BROADCAST — this sends real emails.
                </div>
              )}
              <p className="text-sm text-[#1E1E1E]">You are about to send <strong>Issue #{current.issue_number}</strong> — "{current.subject}" — to <strong data-testid="newsletter-send-count">{subCount}</strong> active subscriber{subCount === 1 ? "" : "s"}.</p>
              <label className="kicker mt-4 block">Type SEND to confirm</label>
              <input value={sendConfirm} onChange={(e) => setSendConfirm(e.target.value)} data-testid="newsletter-send-confirm-input" autoComplete="off"
                className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 focus:outline-none focus:border-[#2D2D2D]" placeholder="SEND" />
              <div className="mt-5 flex gap-2 justify-end">
                <button onClick={() => setSendOpen(false)} className={btnGhost}>Cancel</button>
                <button onClick={doSendNow} disabled={sendConfirm !== "SEND" || busy === "send"} data-testid="newsletter-send-confirm-btn"
                  className={`${btn} ${sendEnabled ? "bg-[#B4443A] text-white hover:bg-[#9F362D]" : "bg-[#E8A33D] text-[#1A1A1A] hover:bg-[#D6922C]"}`}>
                  {busy === "send" ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> {sendEnabled ? "Send to subscribers" : "Run simulated send"}</>}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

function Meta({ label, value, big }) {
  return (
    <div>
      <span className="kicker text-[#6E6E6E]">{label}</span>
      <p className={`text-[#1E1E1E] ${big ? "font-head font-semibold text-2xl" : "text-sm mt-0.5"}`}>{value}</p>
    </div>
  );
}

function Modal({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-3" onClick={onClose} data-testid={testId}>
      <div className="w-full sm:max-w-md bg-[#F3F1EC] border-2 border-[#1E1E1E] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#2D2D2D] px-5 py-3 border-b-4 border-[#E8A33D]">
          <h3 className="font-head uppercase tracking-wide text-sm text-[#F3F1EC]">{title}</h3>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
